import { type Key, useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { SuccessCriteriaDisplayOptions } from '@gonfalon/metrics/constants';
import { RatioMetricDefinitionBox } from '@gonfalon/metrics/RatioMetricDefinitionBox';
import {
  Button,
  FieldGroup,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { MetricRandomizationUnits, RandomizationUnitsError } from './components/MetricRandomizationUnits';
import { RequiredLabel } from './components/RequiredLabel';
import type { MetricForm, RatioTermAggregation } from './formUtils';
import { defaultDenominatorForm } from './formUtils';
import { RatioTermSection } from './RatioTermSection';

import defStyles from './MetricDefinitionFormField/MetricDefinitionFormField.module.css';
import styles from './RatioMetricSection.module.css';

interface RatioMetricSectionProps {
  context: UseFormReturn<MetricForm>;
}

export const RatioMetricSection: React.FC<RatioMetricSectionProps> = ({ context }) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = context;

  const denominator = useWatch({ control, name: 'denominator' });
  const successCriteria = useWatch({ control, name: 'successCriteria', exact: true });
  const randomizationUnits = useWatch({ control, name: 'randomizationUnits', exact: true }) ?? [];
  const dataSourceKey = useWatch({ control, name: 'dataSourceKey' });

  // Ensure denominator defaults are set when ratio mode becomes active.
  useEffect(() => {
    if (!denominator) {
      setValue('denominator', defaultDenominatorForm, { shouldDirty: false });
    }
  }, [denominator, setValue]);

  const numeratorUnitAggType = useWatch({ control, name: 'unitAggregationType' });
  const numeratorIsNumeric = useWatch({ control, name: 'isNumeric' });
  const numeratorUnitAggField = useWatch({ control, name: 'unitAggregationField' });

  function getNumeratorAggParts(): { base: string; column?: string } {
    if (numeratorUnitAggType === 'count_distinct') {
      return { base: 'Count distinct', column: numeratorUnitAggField || '[column name]' };
    }
    if (numeratorIsNumeric && numeratorUnitAggType === 'sum') {
      return { base: 'Sum' };
    }
    if (numeratorIsNumeric && numeratorUnitAggType === 'average') {
      return { base: 'Average' };
    }
    return { base: 'Count' };
  }

  function getDenominatorAggParts(): { base: string; column?: string } {
    const agg: RatioTermAggregation = denominator?.aggregationType ?? 'count';
    const field = denominator?.unitAggregationField;
    if (agg === 'count_distinct') {
      return { base: 'Count distinct', column: field || '[column name]' };
    }
    if (agg === 'sum') {
      return { base: 'Sum' };
    }
    if (agg === 'average') {
      return { base: 'Average' };
    }
    return { base: 'Count' };
  }

  const randomizationUnitsOnSelect = (selection: string[], options: Array<{ label: string; value: string }>) => {
    const selected = options.filter((o) => selection.includes(o.value));
    setValue('randomizationUnits', selected, { shouldDirty: true });
  };

  const randomizationUnitsForm = (
    <MetricRandomizationUnits
      initialValues={randomizationUnits.map((ru: { value: string }) => ru.value)}
      onSelect={randomizationUnitsOnSelect}
      dataSourceKey={dataSourceKey}
    />
  );

  function handleSuccessCriteriaChange(key: Key | null) {
    if (key === 'HigherThanBaseline' || key === 'LowerThanBaseline') {
      setValue('successCriteria', key, { shouldDirty: true });
    }
  }

  return (
    <div className={styles.ratioSection}>
      {/* Two-column numerator / denominator layout */}
      <div className={styles.termColumns}>
        <RatioTermSection context={context} label="Numerator" isNumerator />
        <RatioTermSection context={context} label="Denominator" isNumerator={false} />
      </div>

      {/* Metric definition + analysis unit + success criteria, bordered top and bottom */}
      <div className={styles.definitionSection}>
        <FieldGroup>
          <RequiredLabel label="Metric definition" htmlFor="ratioMetricDefinition" />
          <RatioMetricDefinitionBox
            id="ratioMetricDefinition"
            numeratorAggLabel={getNumeratorAggParts().base}
            numeratorAggColumn={getNumeratorAggParts().column}
            denominatorAggLabel={getDenominatorAggParts().base}
            denominatorAggColumn={getDenominatorAggParts().column}
            units={randomizationUnits.map((ru: { value: string }) => ru.value)}
          />
        </FieldGroup>

        <div className={styles.formRow}>
          <Label className={styles.formRowLabel}>Analysis unit</Label>
          {randomizationUnitsForm}
        </div>
        <RandomizationUnitsError message={errors.randomizationUnits?.message} dataSourceKey={dataSourceKey} />

        <div className={styles.formRow}>
          <Label className={styles.formRowLabel}>Success criteria</Label>
          <Controller
            control={control}
            name="successCriteria"
            render={() => (
              <Select
                onChange={handleSuccessCriteriaChange}
                value={successCriteria ?? 'HigherThanBaseline'}
                aria-label="Choose a success criteria"
              >
                <Button data-test-id="success-criteria-button">
                  <SelectValue />
                  <Icon name="chevron-down" size="small" />
                </Button>
                <Popover className={defStyles.popover}>
                  <ListBox>
                    {SuccessCriteriaDisplayOptions.map((option) => (
                      <ListBoxItem key={option.value} id={option.value}>
                        {option.displayName}
                      </ListBoxItem>
                    ))}
                  </ListBox>
                </Popover>
              </Select>
            )}
          />
        </div>
      </div>
    </div>
  );
};
